// i18n

import './locales/i18n';

// highlight
import './utils/highlight';

// scroll bar
import 'simplebar/dist/simplebar.min.css';

// map
import 'mapbox-gl/dist/mapbox-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';

// Check our docs
// https://docs-minimals.vercel.app/authentication/ts-version

import { AuthProvider } from './contexts/JWTContext';
import { setSession } from './utils/jwt';

//
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { ApolloProvider } from "@apollo/client/react";
import { client } from './graphql';
import { MagicLinkProvider } from './contexts/MagicLinkContext';
import { customHistory } from './utils/history';
import HistoryRouter from './routes/HistoryRouter';

import * as Sentry from '@sentry/react';

// env
import { env } from './config/env';
import { toOperationFirst } from './utils/sentryGrouping';
import { beforeSend } from './utils/sentryBeforeSend';

// ----------------------------------------------------------------------

Sentry.init({
    dsn: env.sentry.dsn,
    environment: env.sentry.environment,
    debug: false,
    enabled: process.env.NODE_ENV !== 'development',     // safety lock while developing
    integrations: [
        Sentry.browserTracingIntegration({
            traceFetch: false,
            traceXHR: false,
            enableLongTask: false,
            enableLongAnimationFrame: false,
            ignoreResourceSpans: ['resource.img', 'resource.css', 'resource.script', 'resource.link', 'resource.other'],
        }),
        Sentry.graphqlClientIntegration({
            endpoints: [env.backend.gqlApi, /\/api\/gql$/]
        })
    ],
    tracesSampleRate: 0.25,
    normalizeDepth: 8,
    beforeSend,
    beforeBreadcrumb: (breadcrumb) => {
        const graphqlDocument = breadcrumb.data?.['graphql.document'];
        if (breadcrumb.data && typeof graphqlDocument === 'string') {
            const { document, operation } = toOperationFirst(graphqlDocument);
            breadcrumb.data['graphql.document'] = document;
            if (operation) breadcrumb.data['graphql.operation'] = operation;
        }
        return breadcrumb;
    },
})


// The mobile group-session WebView is loaded at /ml/group-session with the auth
// token in the URL. `AuthProvider` bootstraps the auth context from localStorage
// synchronously when it mounts — but that runs before the lazy-loaded page can
// call `setSession()` from an effect, so the provider would see no token, log the
// session out, and the WebView would bounce to the login page. Seed the token
// here, before render, so the provider's bootstrap reads an authenticated session.
// Gated to the group-session path so we never `jwtDecode` a non-JWT `token` param
// used by other magic links.
try {
    if (window.location.pathname.endsWith('/ml/group-session')) {
        const urlToken = new URLSearchParams(window.location.search).get('token');
        if (urlToken) {
            setSession(urlToken);
        }
    }
} catch {
    // best-effort: never block app startup on token seeding
}


const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);


root.render(
    <ApolloProvider client={client}>
        <HistoryRouter history={customHistory}>
            <AuthProvider>
                <MagicLinkProvider>
                    <HelmetProvider>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <SettingsProvider>
                                <CollapseDrawerProvider>
                                    <App />
                                </CollapseDrawerProvider>
                            </SettingsProvider>
                        </LocalizationProvider>
                    </HelmetProvider>
                </MagicLinkProvider>
            </AuthProvider>
        </HistoryRouter>
    </ApolloProvider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
